import { httpsCallable } from 'firebase/functions';
import { getFirebaseFunctions } from '@/config/firebase';
import { ensureSessionReady } from '@/services/auth';
import type {
  AdvanceRunResponse,
  ResolveRunResult,
  RunAction,
  TranscribeAudioResponse,
  UploadRunProofResponse,
} from '../runTypes';

/**
 * The first read gates the whole screen, so it gets a tighter bound than the
 * SDK's 70s default. Uploads keep the default — a slow uplink is normal there.
 */
const RESOLVE_TIMEOUT_MS = 25000;

async function call<TReq, TRes>(name: string, data: TReq, timeout?: number): Promise<TRes> {
  await ensureSessionReady();
  const fn = httpsCallable<TReq, TRes>(
    getFirebaseFunctions(),
    name,
    timeout ? { timeout } : undefined,
  );
  const result = await fn(data);
  return result.data;
}

export async function resolveRunForWeb(shortId: string): Promise<ResolveRunResult> {
  return call('resolveRunForWeb', { shortId }, RESOLVE_TIMEOUT_MS);
}

export type RunLinkResult =
  | { ok: true; shortId: string; url: string }
  | { ok: false; reason: string };

/**
 * The run's execute link, minted if the owner never shared one.
 *
 * The home lists runs by id while every run callable is keyed by shortId; the
 * stamped executor trades one for the other and the normal run flow takes over.
 */
export async function getRunLinkForRun(runId: string): Promise<RunLinkResult> {
  return call('generateRunLink', { runId }, RESOLVE_TIMEOUT_MS);
}

export async function advanceRunStepFromWeb(input: {
  runId: string;
  actionId: string;
  action: RunAction;
}): Promise<AdvanceRunResponse> {
  return call('advanceRunStep', input);
}

export async function uploadRunProofFromWeb(input: {
  shortId: string;
  stepId: string;
  contentType: string;
  base64: string;
}): Promise<UploadRunProofResponse> {
  return call('uploadRunProofFromWeb', input);
}

export async function transcribeAudioFromWeb(input: {
  shortId: string;
  contentType: string;
  base64: string;
}): Promise<TranscribeAudioResponse> {
  return call('transcribeAudioFromWeb', input);
}

export async function ensureContactForPhoneAuth(): Promise<{ ok: boolean }> {
  return call('ensureContactForPhoneAuth', {});
}

export function newActionId(): string {
  return `web_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}
